import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useAsyncState } from "@vueuse/core";
import { delay } from "@shared-utils/date.utils";
import UseAsyncResourceOptionsInterface from "@shared-interfaces/use.async.resource.options.interface";

/** En dessous de cette durée, un indicateur n'aurait que le temps de clignoter. */
export const LOADING_MIN_DURATION = 500;

/**
 * Les données déjà chargées, par écran. La route sert de clé : rien à nommer à la main, et deux
 * écrans ne peuvent pas se télescoper. Vidé à la déconnexion par `resetAllAsyncResources()`.
 */
const cache = new Map<string, unknown>();

/** Les `v-model` mutent la donnée : sans copie, l'utilisateur écrirait dans le cache. */
function clone<T>(value: T): T
{
	return JSON.parse(JSON.stringify(value)) as T;
}

export default function useAsyncResource<T>(fetcher: () => Promise<T>, initial: T, options?: UseAsyncResourceOptionsInterface)
{
	const route = useRoute();
	const key = (options?.key ?? route.path) + (options?.scope ? `:${ options.scope }` : "");
	const useCache = options?.cache !== false;

	// Réactif, et non figé au setup : sur ionic la page reste montée d'une visite à l'autre, et un
	// drapeau figé rallumerait l'indicateur à chaque rafraîchissement.
	const hasLoaded = ref<boolean>(useCache && cache.has(key));

	// useAsyncState = VueUse
	const { state: data, isLoading: isFetching, error, execute: reload } = useAsyncState(
		async () =>
		{
			const fetched = hasLoaded.value
				? await fetcher()
				: (await Promise.all([ fetcher(), delay(LOADING_MIN_DURATION) ]))[0];

			if (useCache)
			{
				cache.set(key, fetched);
			}

			return fetched;
		},
		hasLoaded.value ? clone(cache.get(key) as T) : initial,
		{
			// Les valeurs restent affichées jusqu'à l'arrivée des nouvelles, sinon les champs se
			// vident puis se remplissent. `shallow: false` parce que les `v-model` mutent en profondeur.
			resetOnExecute: false,
			shallow: false,
			// Sans quoi VueUse passe par `reportError`, qui affiche en exception non interceptée une
			// erreur que l'écran gère déjà.
			onError: options?.onError ?? console.warn
		}
	);

	// Pas à la fin de chaque requête : quand deux chargements se chevauchent (création du composant
	// puis retour sur l'écran ionic), VueUse jette le résultat du premier, et l'écran s'afficherait
	// vide le temps du second.
	watch(isFetching, (fetching) =>
	{
		if (! fetching && ! error.value && useCache)
		{
			hasLoaded.value = true;
		}
	});

	return {
		data,
		isLoading: computed<boolean>(() => ! hasLoaded.value && isFetching.value),
		// Un écran qui n'a rien à montrer doit pouvoir dire si c'est parce qu'il n'y a rien, ou
		// parce que le chargement a échoué.
		error,
		reload
	};
}

/** Une action déclenchée par l'utilisateur : enregistrer, supprimer, envoyer. */
export function useAsyncAction()
{
	const isRunning = ref<boolean>(false);

	const run = async <R>(operation: () => Promise<R>): Promise<R> =>
	{
		isRunning.value = true;

		try
		{
			return (await Promise.all([ operation(), delay(LOADING_MIN_DURATION) ]))[0];
		}
		finally
		{
			isRunning.value = false;
		}
	};

	return { isRunning, run };
}

export function resetAllAsyncResources(): void
{
	cache.clear();
}

/** Oublie un écran (sa route ou sa `key`) : il repartira d'un skeleton plutôt que d'un état périmé. */
export function clearAsyncResource(key: string): void
{
	cache.delete(key);
}